/**
 * /product-launch-checklist — SEO landing page targeting the "product
 * launch checklist" / "go to market checklist" / "launch plan checklist"
 * cluster.
 *
 * Differentiation angle: a launch checklist in Notion or a Google Doc is
 * a static set of checkboxes — you tick them once, but the moment an
 * upstream decision moves (pricing changes, the spec gets re-cut), nobody
 * goes back to un-tick the items that depended on it, so the green
 * checklist quietly lies. This page hands the reader the actual checklist
 * (Pre-launch / Launch day / Post-launch, spelled out) so a searcher gets
 * something usable, then shows that Draftlize makes each item a card wired
 * to its dependencies, so an upstream change auto-flags the relevant items
 * stale.
 *
 * English-only + inline (no i18n), mirroring DecisionLogTemplate.tsx and
 * the comparison pages. Chinese variant lives in
 * ProductLaunchChecklistZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function ProductLaunchChecklist() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/product-launch-checklist"
      documentTitle="Product Launch Checklist (That Tracks What Changed) · Draftlize"
      documentDescription="A product launch checklist you can copy — Pre-launch, Launch day, Post-launch, every item spelled out. The catch: a checklist in Notion or a Google Doc is static, so when an upstream decision changes (you re-price, you re-cut the spec) nobody un-ticks the items that depended on it. In Draftlize every checklist item is a card wired to its dependencies, so the relevant items auto-flag stale the moment something upstream moves."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Template · Launch Plans</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            A product launch checklist<br/>
            <em>that knows when an item went stale.</em>
          </h1>
          <p className="lede">
            You came for a <em>product launch checklist</em> — the phases, the items, something to paste into a doc before you ship. It's below; take it. But know what you're getting: a checklist is a list of checkboxes, and a checkbox only records that something was true <em>once</em>. The instant an upstream decision moves — you re-price, you re-cut the spec, GTM shifts a week — the items that depended on it are quietly wrong, but they're still green, because nothing in a Notion table or a Google Doc connects "pricing decided" to "pricing page shipped." Draftlize gives you the same checklist — and makes each item a card wired to what it depends on, so the relevant items flag themselves stale the moment something upstream changes.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The checklist</span>
            <h2 className="serif">Three phases. <em>Copy them anywhere.</em></h2>
          </div>
          <p>This is the whole launch plan checklist — Pre-launch, Launch day, Post-launch. It works in a doc, a wiki, or a project board; use it as-is. The next section is about what changes when each item stops being a checkbox and becomes a card that knows what it depends on.</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              phase: 'Pre-launch',
              hint: 'Before you ship',
              items: [
                ['Spec frozen', 'The build scope is locked and written down. Every item below assumes this spec — which is exactly why re-cutting it after the fact silently invalidates half the list.'],
                ['PRD signed off', 'The people who own the outcome have agreed on what ships and what doesn\'t. Not a Slack thumbs-up — a recorded decision you can point back to.'],
                ['Pricing decided', 'Plan, price, and packaging are final. The single most upstream decision on the page: pricing page, billing, paywall copy, and the GTM narrative all hang off it.'],
                ['Billing & paywall wired', 'Checkout works end to end, the paywall enforces the plan you decided, and a real card gets charged in a test. Downstream of pricing — so it goes stale the instant pricing moves.'],
                ['GTM ready', 'Positioning, launch copy, and the channels (site, email, social, any press) are drafted and scheduled. All of it assumes the price and the feature set hold.'],
                ['QA passed', 'The critical paths are tested on production-like data, the regressions are clear, and the known-issues list is honest about what ships broken.'],
                ['Analytics in place', 'The events that tell you whether launch worked are firing and verified before launch, not bolted on after — you can\'t measure a launch you didn\'t instrument.'],
                ['Rollback ready', 'A tested way to undo the deploy, and a written line for who calls it and when. The cheapest item to skip and the most expensive to be missing at 2am.'],
              ],
            },
            {
              phase: 'Launch day',
              hint: 'The window',
              items: [
                ['Deploy', 'Ship to production behind whatever gate you planned — flag, gradual rollout, or full cutover — and confirm the build that went out is the one you signed off.'],
                ['Smoke-test prod', 'Run the critical paths against live production once it\'s out: sign up, pay, do the core action. The five minutes that catch the config that was only wrong in prod.'],
                ['Announce', 'Flip the site, send the email, post the threads — in the order you planned, only after the smoke test is green. Announcing a broken build is the one mistake you can\'t un-send.'],
                ['Monitor', 'Watch errors, latency, signups, and payments live for the first hours. Someone is explicitly on call and watching the dashboards, not assuming green.'],
              ],
            },
            {
              phase: 'Post-launch',
              hint: 'After the dust',
              items: [
                ['Metrics review', 'Read the launch against the numbers you instrumented — activation, conversion, the one metric you said would define success. Compare to what the PRD predicted.'],
                ['Collect feedback', 'Pull the first wave of user reactions, support tickets, and confusion into one place where it can turn into decisions, not vanish into Slack.'],
                ['Retro', 'What the checklist missed, what went stale that nobody caught, what to change before the next launch. The item that makes the next launch cheaper.'],
                ['Fold learnings back', 'Turn what you learned into updated decisions and specs — so the next launch starts from current truth, not from a doc that froze on launch day.'],
              ],
            },
          ].map((group, gi) => (
            <div key={group.phase}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  padding: '16px 22px',
                  borderTop: gi === 0 ? 0 : '1px solid var(--ink-0)',
                  background: 'var(--paper-1)',
                }}
              >
                <span className="serif" style={{ fontSize: 21, fontWeight: 500, color: 'var(--ink-0)' }}>
                  {group.phase}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {group.hint}
                </span>
              </div>
              {group.items.map(([k, body]) => (
                <div
                  key={k}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '24px 220px 1fr',
                    gap: 18,
                    padding: '18px 22px',
                    borderTop: '1px dashed var(--rule)',
                    alignItems: 'baseline',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 15,
                      color: 'var(--ink-3)',
                      lineHeight: 1.2,
                    }}
                  >
                    ☐
                  </span>
                  <span
                    className="serif"
                    style={{ fontSize: 18, fontWeight: 500, color: 'var(--ink-0)', lineHeight: 1.25 }}
                  >
                    {k}
                  </span>
                  <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{body}</p>
                </div>
              ))}
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          That's the checklist. Below: why ticking a box is the moment it starts to lie — and what we do about it.
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why a checklist <em>goes stale.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">A checkbox records the past, not the present</h3>
            <p>Ticking "pricing decided" is true at the moment you tick it and never re-checked after. A checklist in Notion or a Google Doc captures a state once; it has no idea when the thing it recorded stopped being true, because a list of checkboxes can't watch the rest of the launch change.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">One upstream change, and nobody un-ticks the rest</h3>
            <p>You re-price three days before launch. Now the pricing page, the billing wiring, the paywall copy, and the GTM narrative are all wrong — but every one of those items is still green, because nothing connects "pricing decided" to the four items downstream of it. The checklist that was supposed to protect the launch is the thing that hides the breakage.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">All green is the most dangerous state</h3>
            <p>A fully-ticked launch checklist <em>feels</em> like readiness, which is exactly why it's risky: you can't tell which of the green items went stale after you ticked them. The whole list reads as done while a real fraction of it is fiction — and you find out which fraction during the launch, not before.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same checklist, <em>made live.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Each item becomes a card, not a checkbox</h3>
            <p>In Draftlize the launch checklist isn't a list of ticks — each item is a typed card: the decision or spec it represents, who owns it, what state it's in, and what it depends on. Structured, addressable by ID, and citable from the PRD or a thread instead of buried in a doc.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Change pricing, the dependent items flag stale</h3>
            <p>Because each item's dependencies are real links and not implied, re-pricing turns every card that rested on the old price — pricing page, billing, paywall, GTM — stale automatically, the way a build system invalidates everything downstream of a changed file. The green you can't trust by hand, the substrate keeps honest.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">An AI agent reads and works the list</h3>
            <p>Claude Code or Cursor, over MCP, reads every relevant decision and spec before it drafts the launch copy or the release notes, and writes status back into the same cards. The checklist stops being a doc someone forgets to update and becomes context the agent re-reads on every turn.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A checklist tells you what to do before launch. It can't tell you when something you already ticked stopped being true.
        </blockquote>
        <cite>Keep the three phases. Let the substrate keep them honest.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is a product launch checklist?', a: 'A product launch checklist is the list of everything that has to be true before, during, and after a launch — across product, marketing, sales, support, and legal — so nothing critical is missed under deadline pressure. It turns a chaotic launch week into a set of owned, verifiable items.' },
            { q: 'What should a product launch checklist include?', a: 'Three phases. Pre-launch: final QA, docs, pricing and billing, marketing assets, support enablement, legal sign-off. Launch: deploy, announce, monitor. Post-launch: gather feedback, watch metrics, fix fast. Each item needs an owner and a clear done state, or it becomes a tick nobody actually verified.' },
            { q: 'What are the phases of a product launch?', a: 'Pre-launch (build readiness and line everything up), launch (ship, announce, and watch), and post-launch (measure, learn, and iterate). Most launch pain comes from treating the checklist as pre-launch only and going quiet after ship day, when the metrics that decide success actually arrive.' },
            { q: 'Why is an all-green checklist risky, and how does Draftlize help?', a: 'A fully ticked list feels like readiness, but you cannot tell which green items went stale after you ticked them. Draftlize makes each item a card with real dependencies, so re-pricing turns every item that rested on the old price stale automatically — the green stays honest instead of hiding fiction until launch day.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Run your next launch <em>free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Take the checklist above, or spin up a project and let the agent fill it in from your spec and PRD — then keep every item, and everything it depends on, current for you straight through launch day.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
